/**
 * Billing constants: seat/pricing/credit pack limits. Must match flotoapps
 * and flotoframe for Stripe integration consistency.
 */

export const TEAMS_MIN_SEATS = 3;
export const TEAMS_MAX_SEATS = 50;
export const TEAMS_PRICE_PER_SEAT = 10;
export const TEAMS_CREDITS_PER_SEAT = 2500;

export const CreditPackSize = {
  // flotoapps' `CreditPackSize.QUICK`: the same Stripe price
  // (STRIPE_CREDIT_PACK_QUICK_PRICE_ID), whose `metadata.credits` is what
  // flotoframe's webhook adds.
  QUICK: 300,
  PACK1: 1000,
  PACK2: 3500,
  PACK3: 12500,
} as const;

export type CreditPackSize =
  (typeof CreditPackSize)[keyof typeof CreditPackSize];

export const CREDIT_PACK_DETAILS: Record<
  CreditPackSize,
  {
    price: string;
    credits: number;
    label: string;
    savePercentage: string | null;
  }
> = {
  [CreditPackSize.QUICK]: {
    price: '$3',
    credits: 300,
    label: 'Quick top-up',
    savePercentage: null,
  },
  [CreditPackSize.PACK1]: {
    price: '$10',
    credits: 1000,
    label: 'Starter',
    savePercentage: null,
  },
  [CreditPackSize.PACK2]: {
    price: '$30',
    credits: 3500,
    label: 'Popular',
    savePercentage: '14%',
  },
  [CreditPackSize.PACK3]: {
    price: '$100',
    credits: 12500,
    label: 'Best value',
    savePercentage: '20%',
  },
};

/**
 * Floto Rooms' own features. The wider Floto product's (Design Audit, Flow
 * Testing, Design Diff) come with the plan but are used on floto.ai, so the
 * billing page adds a linked "Includes Floto.ai features" line rather than
 * listing them here.
 */
export const FREE_PLAN_FEATURES = [
  '200 credits / week',
  'Unlimited personas',
  'Unlimited rooms',
];

export const PRO_PLAN_FEATURES = [
  'Everything in Free',
  'No rate limits',
  'Buy credit top-up packs',
];

/** Pro's monthly allowance — what `baseCredits` is reset to on renewal, so
 *  the billing page can say how much of it has gone. */
export const PRO_MONTHLY_CREDITS = 1500;

/** Each plan's allowance, as its card's one-line headline. */
export const FREE_PLAN_CREDITS_LINE = '200 credits / week';
export const PRO_PLAN_CREDITS_LINE = `${PRO_MONTHLY_CREDITS.toLocaleString('en-US')} credits / month`;

export const TEAMS_PLAN_FEATURES = [
  `${TEAMS_CREDITS_PER_SEAT.toLocaleString()} credits per seat / month`,
  'Everything in Pro',
  'Pooled credit use across all team members',
  'Collaboration features such as Shared Personas',
  'Priority Support',
];
